import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { 
  UserIcon, 
  ChevronDownIcon, 
  LogOutIcon,
  ShieldIcon,
  SettingsIcon,
  MailIcon,
  GlobeIcon,
  MenuIcon,
  XIcon,
} from 'lucide-react';

const Header = ({ pageTitle, onToggleSidebar, sidebarOpen }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { isRTL, currentLanguage, changeLanguage, getCurrentLanguageInfo, getAvailableLanguages } = useLanguage();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const languageDropdownRef = useRef(null);

  // إغلاق القائمة المنسدلة عند النقر خارجها
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    navigate(user?.role === 'STUDENT' ? '/student/profile' : '/dashboard/profile');
    setIsDropdownOpen(false);
  };

  const handleSecurityClick = () => {
    navigate(user?.role === 'STUDENT' ? '/student/settings' : '/dashboard/settings');
    setIsDropdownOpen(false);
  };

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
    setIsLanguageDropdownOpen(false);
  };

  const getRoleIcon = () => {
    switch (user?.role) {
      case 'SUPER_ADMIN':
        return <ShieldIcon className="w-4 h-4 text-red-600" />;
      case 'ADMIN':
        return <ShieldIcon className="w-4 h-4 text-purple-600" />;
      case 'MANAGER':
        return <ShieldIcon className="w-4 h-4 text-blue-600" />;
      case 'SUPERVISOR':
        return <ShieldIcon className="w-4 h-4 text-green-600" />;
      case 'TRAINER':
        return <ShieldIcon className="w-4 h-4 text-orange-600" />;
      default:
        return <UserIcon className="w-4 h-4 text-gray-600" />;
    }
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    return user?.username?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <header className="relative z-50 border-b border-gray-200 bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-16 min-w-0 items-center justify-between gap-3 py-2 sm:py-0">
          {/* عنوان الصفحة + زر القائمة (موبايل) */}
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            {typeof onToggleSidebar === 'function' ? (
              <button
                type="button"
                onClick={onToggleSidebar}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 lg:hidden"
                aria-expanded={typeof sidebarOpen === 'boolean' ? sidebarOpen : undefined}
                aria-label={
                  sidebarOpen ? t('common.closeNavMenu') : t('common.openNavMenu')
                }
              >
                {sidebarOpen ? <XIcon className="h-5 w-5" aria-hidden /> : <MenuIcon className="h-5 w-5" aria-hidden />}
              </button>
            ) : null}
            <h1
              className={`min-w-0 flex-1 truncate text-lg font-semibold text-gray-900 sm:text-xl ${isRTL ? 'text-right' : 'text-left'}`}
              title={typeof pageTitle === 'string' ? pageTitle : undefined}
            >
              {pageTitle}
            </h1>
          </div>

          {/* معلومات المستخدم والقائمة المنسدلة */}
          <div className="flex items-center space-x-3">
            {/* زر اللغة */}
            <div className="relative" ref={languageDropdownRef}>
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
                title={t('common.language')}
              >
                <GlobeIcon className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  {getCurrentLanguageInfo().flag}
                </span>
                <ChevronDownIcon 
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    isLanguageDropdownOpen ? 'rotate-180' : ''
                  }`} 
                />
              </button>

              {/* قائمة اللغات المنسدلة */}
              {isLanguageDropdownOpen && (
                <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50`}>
                  <div className="py-2">
                    {getAvailableLanguages().map((language) => (
                      <button
                        key={language.code}
                        onClick={() => handleLanguageChange(language.code)}
                        className={`w-full flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                          currentLanguage === language.code 
                            ? 'bg-primary-50 text-primary-700' 
                            : 'text-gray-700'
                        }`}
                      >
                        <span className="text-lg mr-3">{language.flag}</span>
                        <span className="flex-1 text-left">{language.name}</span>
                        {currentLanguage === language.code && (
                          <span className="text-primary-600">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                {/* صورة البروفايل */}
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-600">
                    {getInitials()}
                  </span>
                </div>

                {/* اسم المستخدم */}
                <div className={`hidden sm:block ${isRTL ? 'text-right' : 'text-left'}`}>
                  <p className="text-sm font-medium text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t(`roles.${user?.role}`)}
                  </p>
                </div>

                {/* أيقونة السهم */}
                <ChevronDownIcon 
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    isDropdownOpen ? 'rotate-180' : ''
                  }`} 
                />
              </button>

              {/* القائمة المنسدلة */}
              {isDropdownOpen && (
                <div className={`absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 ${
                  isRTL ? 'left-0' : 'right-0'
                }`}>
                  <div className="p-4">
                    {/* معلومات المستخدم */}
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-lg font-medium text-primary-600">
                          {getInitials()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user?.firstName} {user?.lastName}
                        </p>
                        <div className="flex items-center mt-1">
                          {getRoleIcon()}
                          <span className={`text-xs text-gray-500 ${isRTL ? 'mr-1' : 'ml-1'}`}>
                            {t(`roles.${user?.role}`)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* البريد الإلكتروني */}
                    <div className="flex items-center py-2 px-3 bg-gray-50 rounded-lg mb-4">
                      <MailIcon className="w-4 h-4 text-gray-400" />
                      <span className={`text-sm text-gray-600 ${isRTL ? 'mr-2' : 'ml-2'} truncate`}>
                        {user?.email}
                      </span>
                    </div>

                    {/* روابط القائمة */}
                    <div className="space-y-1">
                      <button
                        onClick={handleProfileClick}
                        className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <UserIcon className="w-4 h-4 text-gray-400" />
                        <span className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                          {t('navigation.profile')}
                        </span>
                      </button>

                      <button
                        onClick={handleSecurityClick}
                        className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <SettingsIcon className="w-4 h-4 text-gray-400" />
                        <span className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                          {t('profile.accountSecurity')}
                        </span>
                      </button>

                      <hr className="my-2" />

                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <LogOutIcon className="w-4 h-4" />
                        <span className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                          {t('common.logout')}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
